import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  DatasetSchemaInfo,
  DatasetSchemaInfoSchema,
  DatasetName,
  DatasetSelectField,
  DatasetFilter,
  DatasetOrderBy,
  DatasetResult,
  DatasetResultSchema,
  DatasetQuery,
  DatasetQuerySchema,
  DatasetAIQueryResponseSchema,
} from "@forgepoint/shared";
import { z } from "zod";
import {
  encodeJsonToBase64,
  decodeBase64ToJson,
  getUrlParam,
  setUrlParam,
  clearUrlParam,
} from "@/lib/urlState";

interface DatasetExplorerStore {
  /** Cached dataset schema metadata. */
  schemas: DatasetSchemaInfo[];
  schemasLoading: boolean;
  schemasError: string | null;

  /** UI mode: chat (landing) or explorer (query builder + results). */
  mode: "chat" | "explorer";
  /** Whether the AI is currently generating a query. */
  chatLoading: boolean;
  /** Last AI generation error. */
  chatError: string | null;
  /** Currently selected dataset. */
  selectedDataset: DatasetName | null;
  /** Enabled join prefixes (UI-only — server resolves joins from field prefixes). */
  enabledJoins: Set<string>;
  /** Fields selected for the query. */
  selectedFields: DatasetSelectField[];
  /** Active filter conditions. */
  filters: DatasetFilter[];
  /** Group-by field references. */
  groupBy: string[];
  /** Result ordering configuration. */
  orderBy: DatasetOrderBy[];
  /** Max rows to return. */
  limit: number;

  /** Query execution result. */
  result: DatasetResult | null;
  resultLoading: boolean;
  resultError: string | null;

  /** Display mode for results. */
  viewMode: "table" | "chart";
  /** Chart type when in chart view. */
  chartType: "bar" | "line";
  /** User-selected x-axis column override (null = auto-detect). */
  chartXAxis: string | null;
  /** User-selected y-axis column overrides (null = auto-detect). */
  chartYAxes: string[] | null;

  /** Sends a natural-language question to the AI and populates the query. */
  askQuestion: (question: string) => Promise<void>;
  /** Switches between chat and explorer modes. */
  setMode: (mode: "chat" | "explorer") => void;
  /** Applies an AI-generated query to the store fields. */
  applyGeneratedQuery: (query: DatasetQuery) => void;
  /** Fetches all dataset schemas from the API. */
  fetchSchemas: () => Promise<void>;
  /** Sets the active dataset and resets all query state. */
  setSelectedDataset: (dataset: DatasetName) => void;
  /** Toggles a join prefix on/off, cleaning up orphaned references. */
  toggleJoin: (prefix: string) => void;
  /** Adds a field to the query select list. */
  addField: (field: DatasetSelectField) => void;
  /** Removes a field from the query select list by index. */
  removeField: (index: number) => void;
  /** Updates a field in the query select list by index. */
  updateField: (index: number, field: DatasetSelectField) => void;
  /** Adds a filter condition. */
  addFilter: (filter: DatasetFilter) => void;
  /** Removes a filter condition by index. */
  removeFilter: (index: number) => void;
  /** Updates a filter condition by index. */
  updateFilter: (index: number, filter: DatasetFilter) => void;
  /** Sets the group-by field references. */
  setGroupBy: (fields: string[]) => void;
  /** Sets the order-by configuration. */
  setOrderBy: (orderBy: DatasetOrderBy[]) => void;
  /** Sets the result row limit. */
  setLimit: (limit: number) => void;
  /** Builds and executes the current query. */
  executeQuery: () => Promise<void>;
  /** Switches between table and chart view. */
  setViewMode: (mode: "table" | "chart") => void;
  /** Sets the chart type. */
  setChartType: (type: "bar" | "line") => void;
  /** Sets the user-selected x-axis column (null to revert to auto-detect). */
  setChartXAxis: (col: string | null) => void;
  /** Sets the user-selected y-axis columns (null to revert to auto-detect). */
  setChartYAxes: (cols: string[] | null) => void;
  /** Resets all query state and returns to chat mode (preserves schemas). */
  resetQuery: () => void;
  /** Hydrates store from URL search params and auto-executes if a valid query is found. */
  hydrateFromUrl: () => Promise<void>;
}

type DatasetExplorerQueryState = Pick<
  DatasetExplorerStore,
  | "mode"
  | "chatLoading"
  | "chatError"
  | "selectedDataset"
  | "enabledJoins"
  | "selectedFields"
  | "filters"
  | "groupBy"
  | "orderBy"
  | "limit"
  | "result"
  | "resultLoading"
  | "resultError"
  | "viewMode"
  | "chartType"
  | "chartXAxis"
  | "chartYAxes"
>;

const INITIAL_QUERY_STATE: DatasetExplorerQueryState = {
  mode: "chat",
  chatLoading: false,
  chatError: null,
  selectedDataset: null,
  enabledJoins: new Set<string>(),
  selectedFields: [],
  filters: [],
  groupBy: [],
  orderBy: [],
  limit: 1000,
  result: null,
  resultLoading: false,
  resultError: null,
  viewMode: "table",
  chartType: "bar",
  chartXAxis: null,
  chartYAxes: null,
};

/**
 * Filters out entries whose field starts with the given prefix.
 * @param prefix - The join prefix to filter against
 * @param field - The field reference to check
 * @returns Whether the field does NOT belong to the prefix
 */
function fieldDoesNotMatchPrefix(prefix: string, field: string): boolean {
  return !field.startsWith(`${prefix}.`);
}

export const useDatasetExplorerStore = create<DatasetExplorerStore>(
  (set, get) => ({
    schemas: [],
    schemasLoading: false,
    schemasError: null,
    ...INITIAL_QUERY_STATE,

    askQuestion: async (question) => {
      set({ chatLoading: true, chatError: null });
      try {
        const response = await fetchApi("datasets/ai-query", "POST", {
          body: { question },
        });
        if (!response.ok || !response.data) {
          set({
            chatError: response.error ?? "Failed to generate query",
            chatLoading: false,
          });
          return;
        }
        const data = DatasetAIQueryResponseSchema.parse(response.data);
        get().applyGeneratedQuery(data.query);
        await get().executeQuery();
      } catch (error) {
        if (error instanceof Error) {
          captureException(error);
        }
        set({ chatError: "Failed to generate query", chatLoading: false });
      }
    },

    setMode: (mode) => set({ mode }),

    applyGeneratedQuery: (query) => {
      const { schemas } = get();
      const schema = schemas.find((s) => s.dataset === query.dataset);
      const rootPrefix = schema?.rootPrefix ?? query.dataset;

      const allFieldRefs = [
        ...query.select.map((f) => f.field),
        ...(query.filters ?? []).map((f) => f.field),
        ...(query.groupBy ?? []),
        ...(query.orderBy ?? []).map((o) => o.field),
      ];

      const joinPrefixes = new Set<string>();
      for (const ref of allFieldRefs) {
        const prefix = ref.split(".")[0];
        if (prefix && prefix !== rootPrefix) {
          joinPrefixes.add(prefix);
        }
      }

      set({
        selectedDataset: query.dataset,
        selectedFields: query.select,
        filters: query.filters ?? [],
        groupBy: query.groupBy ?? [],
        orderBy: query.orderBy ?? [],
        limit: query.limit ?? 1000,
        enabledJoins: joinPrefixes,
        mode: "explorer",
        chatLoading: false,
        chatError: null,
      });
    },

    fetchSchemas: async () => {
      set({ schemasLoading: true, schemasError: null });
      try {
        const response = await fetchApi("datasets/schema", "GET");
        if (!response.ok || !response.data) {
          set({
            schemasError: response.error ?? "Failed to fetch schemas",
            schemasLoading: false,
          });
          return;
        }
        const SchemasResponseSchema = z.object({
          schemas: z.array(DatasetSchemaInfoSchema),
        });
        const data = SchemasResponseSchema.parse(response.data);
        set({ schemas: data.schemas, schemasLoading: false });
      } catch (error) {
        if (error instanceof Error) {
          captureException(error);
        }
        set({
          schemasError: "Failed to fetch schemas",
          schemasLoading: false,
        });
      }
    },

    setSelectedDataset: (dataset) => {
      set({
        ...INITIAL_QUERY_STATE,
        mode: get().mode,
        selectedDataset: dataset,
        enabledJoins: new Set<string>(),
      });
    },

    toggleJoin: (prefix) => {
      const { enabledJoins, selectedFields, filters, groupBy, orderBy } = get();
      const next = new Set(enabledJoins);

      if (next.has(prefix)) {
        next.delete(prefix);

        const removedFields = selectedFields.filter(
          (f) => !fieldDoesNotMatchPrefix(prefix, f.field),
        );
        const removedAliases = new Set(
          removedFields.map((f) => f.alias).filter(Boolean),
        );

        set({
          enabledJoins: next,
          selectedFields: selectedFields.filter((f) =>
            fieldDoesNotMatchPrefix(prefix, f.field),
          ),
          filters: filters.filter((f) =>
            fieldDoesNotMatchPrefix(prefix, f.field),
          ),
          groupBy: groupBy.filter((f) => fieldDoesNotMatchPrefix(prefix, f)),
          orderBy: orderBy.filter(
            (o) =>
              fieldDoesNotMatchPrefix(prefix, o.field) &&
              !removedAliases.has(o.field),
          ),
        });
      } else {
        next.add(prefix);
        set({ enabledJoins: next });
      }
    },

    addField: (field) => {
      set((state) => ({
        selectedFields: [...state.selectedFields, field],
      }));
    },

    removeField: (index) => {
      set((state) => ({
        selectedFields: state.selectedFields.filter((_, i) => i !== index),
      }));
    },

    updateField: (index, field) => {
      set((state) => ({
        selectedFields: state.selectedFields.map((f, i) =>
          i === index ? field : f,
        ),
      }));
    },

    addFilter: (filter) => {
      set((state) => ({ filters: [...state.filters, filter] }));
    },

    removeFilter: (index) => {
      set((state) => ({
        filters: state.filters.filter((_, i) => i !== index),
      }));
    },

    updateFilter: (index, filter) => {
      set((state) => ({
        filters: state.filters.map((f, i) => (i === index ? filter : f)),
      }));
    },

    setGroupBy: (fields) => set({ groupBy: fields }),
    setOrderBy: (orderBy) => set({ orderBy }),
    setLimit: (limit) => set({ limit }),

    executeQuery: async () => {
      const {
        selectedDataset,
        selectedFields,
        filters,
        groupBy,
        orderBy,
        limit,
      } = get();
      if (!selectedDataset || selectedFields.length === 0) {
        return;
      }

      set({ resultLoading: true, resultError: null });

      try {
        // Auto-include all non-aggregated selected fields in groupBy when aggregates are present
        const hasAggregates = selectedFields.some((f) => f.aggregate);
        const resolvedGroupBy = hasAggregates
          ? Array.from(
              new Set([
                ...groupBy,
                ...selectedFields
                  .filter((f) => !f.aggregate)
                  .map((f) => f.field),
              ]),
            )
          : groupBy;

        const query = {
          dataset: selectedDataset,
          select: selectedFields,
          ...(filters.length > 0 ? { filters } : {}),
          ...(resolvedGroupBy.length > 0 ? { groupBy: resolvedGroupBy } : {}),
          ...(orderBy.length > 0 ? { orderBy } : {}),
          limit,
        };

        const response = await fetchApi("datasets/query", "POST", {
          body: query,
        });

        if (!response.ok || !response.data) {
          set({
            resultError: response.error ?? "Query execution failed",
            resultLoading: false,
          });
          return;
        }

        set({
          result: DatasetResultSchema.parse(response.data),
          resultLoading: false,
          viewMode: "table",
          chartXAxis: null,
          chartYAxes: null,
        });

        setUrlParam("q", encodeJsonToBase64(query));
      } catch (error) {
        if (error instanceof Error) {
          captureException(error);
        }
        set({
          resultError: "Query execution failed",
          resultLoading: false,
        });
      }
    },

    setViewMode: (mode) => set({ viewMode: mode }),
    setChartType: (type) => set({ chartType: type }),
    setChartXAxis: (col) => set({ chartXAxis: col }),
    setChartYAxes: (cols) => set({ chartYAxes: cols }),

    resetQuery: () => {
      set({
        ...INITIAL_QUERY_STATE,
        enabledJoins: new Set<string>(),
        mode: "chat",
        chatLoading: false,
        chatError: null,
      });
      clearUrlParam("q");
    },

    hydrateFromUrl: async () => {
      const encoded = getUrlParam("q");
      if (!encoded) {
        return;
      }
      const decoded = decodeBase64ToJson(encoded);
      const result = DatasetQuerySchema.safeParse(decoded);
      if (!result.success) {
        return;
      }
      get().applyGeneratedQuery(result.data);
      await get().executeQuery();
    },
  }),
);
